"use client";

import { motion, useMotionValue, useSpring } from "motion/react";
import { type PointerEvent, type ReactNode, useRef } from "react";

import { cn } from "@/lib/utils";

// =============================================================================

type MagneticProps = {
  children: ReactNode;
  className?: string;
  /** Share of the pointer offset followed by the content (0 → 1) */
  strength?: number;
};

/**
 * The content is attracted by the pointer while hovered (mouse only).
 */
export function Magnetic({
  children,
  className,
  strength = 0.3,
}: MagneticProps) {
  const ref = useRef<HTMLDivElement>(null);
  const x = useSpring(useMotionValue(0), {
    stiffness: 220,
    damping: 15,
    mass: 0.2,
  });
  const y = useSpring(useMotionValue(0), {
    stiffness: 220,
    damping: 15,
    mass: 0.2,
  });

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse" || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((event.clientX - (rect.left + rect.width / 2)) * strength);
    y.set((event.clientY - (rect.top + rect.height / 2)) * strength);
  };

  const reset = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onPointerMove={onPointerMove}
      onPointerLeave={reset}
      style={{ x, y }}
      className={cn("inline-block", className)}
    >
      {children}
    </motion.div>
  );
}

// =============================================================================
